<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/components/ui/toast'
import { useDocumentsStore } from '../store'
import { draftKey, saveDraft, loadDraft, clearDraft, useMinimizedDocuments } from '@/composables/useMinimizedDocuments'
import { MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import type { Document, DocumentEntityType, DocumentType } from '@/types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Loader2, Minus, Copy, Workflow, Square, Code2 } from 'lucide-vue-next'
import { DIAGRAM_TYPES } from './diagramTypes'

const props = defineProps<{
  open: boolean
  entityType: DocumentEntityType
  entityId: string
  document?: Document | null
  initialFormState?: { title: string; type: DocumentType; content: string; isPublic: boolean } | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'success'): void
  (e: 'minimize', payload: { title: string; type: DocumentType; content: string; isPublic: boolean }): void
}>()

const { t } = useI18n()
const { toast } = useToast()
const documentsStore = useDocumentsStore()
const { setActiveDocument, updateActiveForm, clearActiveDocument } = useMinimizedDocuments()

const isSubmitting = ref(false)
const activeEditor = ref<any>(null)
const showEditorModal = ref(false)

watch(() => showEditorModal.value, (val) => {
  if (!val) {
    setTimeout(() => {
      activeEditor.value = null
      editingBlockIndex.value = null
      localStorage.removeItem('editingDiagramCode')
    }, 300)
  }
})
const editingBlockIndex = ref<number | null>(null)

const existingDiagrams = computed(() => {
  const regex = /```mermaid\n([\s\S]*?)```/g;
  let match;
  const blocks = [];
  let index = 0;
  while ((match = regex.exec(form.value.content)) !== null) {
    const fullMatch = match[0];
    const code = match[1].trim();
    
    // Find the first actual line of code that is not a comment and not empty
    const lines = code.split('\n').map(l => l.trim());
    const firstActualLine = lines.find(l => l && !l.startsWith('%%'));
    const firstWord = firstActualLine ? firstActualLine.split(' ')[0] : '';
    
    let typeId = 'live';
    if (firstWord.includes('classDiagram')) typeId = 'class';
    else if (firstWord.includes('flowchart') || firstWord.includes('graph')) typeId = 'flow';
    else if (firstWord.includes('architecture')) typeId = 'architecture';
    else if (firstWord.includes('gantt')) typeId = 'gantt';
    else if (firstWord.includes('erDiagram')) typeId = 'er';
    else if (firstWord.includes('pie')) typeId = 'pie';
    else if (firstWord.includes('timeline')) typeId = 'timeline';
    else if (firstWord.includes('journey')) typeId = 'journey';
    else if (firstWord.includes('sequenceDiagram')) typeId = 'sequence';
    else if (firstWord.includes('requirementDiagram')) typeId = 'requirement';
    else if (firstWord.includes('xychart')) typeId = 'xychart';
    else if (firstWord.includes('stateDiagram')) typeId = 'state';
    else if (firstWord.includes('kanban')) typeId = 'kanban';
    else if (firstWord.includes('treeView') || firstWord.includes('tree')) typeId = 'tree';
    else if (firstWord.includes('block-beta')) typeId = 'eventmodeling';
    
    const diagramDef = DIAGRAM_TYPES.find(d => d.id === typeId) || DIAGRAM_TYPES.find(d => d.id === 'live');
    
    const titleMatch = code.match(/^%%\s*title:\s*(.+)$/m);
    const title = titleMatch ? titleMatch[1].trim() : '';
    
    blocks.push({
      index: index++,
      raw: fullMatch,
      code: code,
      title: title,
      startIndex: match.index,
      endIndex: match.index + fullMatch.length,
      diagram: diagramDef
    });
  }
  return blocks;
});

const openEditor = (diagramType: any) => {
  editingBlockIndex.value = null;
  localStorage.removeItem('editingDiagramCode');
  activeEditor.value = diagramType
  showEditorModal.value = true
}

const editExistingDiagram = (block: any) => {
  editingBlockIndex.value = block.index;
  localStorage.setItem('editingDiagramCode', block.code);
  activeEditor.value = block.diagram;
  showEditorModal.value = true;
}

const handleDiagramSave = (mermaidCode: string) => {
  // Garantizar que no haya doble bloque de markdown
  let rawCode = mermaidCode.trim();
  const match = rawCode.match(/```mermaid\n([\s\S]*?)```/);
  if (match) {
    rawCode = match[1].trim();
  } else {
    rawCode = rawCode.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '').trim();
  }
  
  if (editingBlockIndex.value !== null) {
    const block = existingDiagrams.value.find(b => b.index === editingBlockIndex.value);
    if (block) {
      // Preservar el título si existía en el bloque anterior: los editores
      // visuales regeneran el código y no conservan la línea `%% title:`
      let finalCode = rawCode;
      const oldTitleMatch = block.code.match(/^%%\s*title:\s*(.+)$/m);
      if (oldTitleMatch) {
        finalCode = finalCode.replace(/^%%\s*title:.*$\n?/m, '');
        finalCode = `%% title: ${oldTitleMatch[1].trim()}\n` + finalCode;
      }

      const formattedCode = '```mermaid\n' + finalCode + '\n```';
      const before = form.value.content.substring(0, block.startIndex);
      const after = form.value.content.substring(block.endIndex);
      form.value.content = before + formattedCode + after;
    }
    editingBlockIndex.value = null;
  } else {
    const formattedCode = '```mermaid\n' + rawCode + '\n```';
    form.value.content += (form.value.content ? '\n\n' : '') + formattedCode;
  }
}
const form = ref({
  title: '',
  type: 'other' as DocumentType,
  content: '',
  isPublic: true,
})

const isEditing = computed(() => !!props.document)

const copyDocumentId = async () => {
  if (!props.document) return
  try {
    await navigator.clipboard.writeText(props.document.id)
    toast({ title: 'ID copiado', description: 'El ID del documento se copió al portapapeles' })
  } catch {
    toast({ title: 'Error', description: 'No se pudo copiar el ID', variant: 'destructive' })
  }
}

const currentDraftKey = computed(() =>
  draftKey(props.entityType, props.entityId, props.document?.id)
)

const documentTypeOptions: { value: DocumentType; label: string }[] = [
  { value: 'scope', label: 'Alcance' },
  { value: 'architecture', label: 'Arquitectura' },
  { value: 'specifications', label: 'Especificaciones' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Otro' },
]

let codeBlockObserver: MutationObserver | null = null;

const injectEditButtonsToDOM = () => {
  const container = document.querySelector('.md-editor-preview');
  if (!container) return;
  
  // Encontrar wrappers de codigo de md-editor-v3
  const codeBlocks = container.querySelectorAll('.md-editor-code-block');
  
  codeBlocks.forEach((block) => {
    // Si ya tiene el boton, ignorar
    if (block.querySelector('.btn-edit-mermaid')) return;
    
    // Buscar la etiqueta de lenguaje
    const langSpan = block.querySelector('.md-editor-code-head > span');
    if (langSpan && langSpan.textContent?.trim() === 'mermaid') {
      const head = block.querySelector('.md-editor-code-head');
      if (head) {
        // Crear boton
        const btn = document.createElement('span');
        btn.className = 'btn-edit-mermaid md-editor-code-action';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-workflow" style="margin-right:4px;"><rect width="8" height="8" x="3" y="3" rx="2"/><path d="M7 11v4a2 2 0 0 0 2 2h4"/><rect width="8" height="8" x="13" y="13" rx="2"/></svg> Editar`;
        btn.style.cursor = 'pointer';
        btn.style.color = 'var(--primary)';
        btn.style.display = 'inline-flex';
        btn.style.alignItems = 'center';
        btn.style.marginLeft = '12px';
        btn.style.fontWeight = 'bold';
        
        btn.onclick = () => {
          const codeEl = block.querySelector('pre code');
          if (codeEl) {
            const rawText = codeEl.textContent?.trim() || '';
            const existing = existingDiagrams.value.find(b => b.code.trim() === rawText);
            if (existing) {
              editExistingDiagram(existing);
            }
          }
        };
        
        head.appendChild(btn);
      }
    }
  });
}

const setupCodeBlockObserver = () => {
  if (codeBlockObserver) codeBlockObserver.disconnect();
  
  const container = document.querySelector('.md-editor');
  if (container) {
    codeBlockObserver = new MutationObserver(() => {
      injectEditButtonsToDOM();
    });
    codeBlockObserver.observe(container, { childList: true, subtree: true });
    injectEditButtonsToDOM();
  }
}

// --- Initialize form when dialog opens ---
// `immediate: true` is critical: when the widget renders this component
// via v-if with open=true, the watch must fire on the initial value.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      if (props.initialFormState) {
        // Restoring from minimized — use the provided state
        form.value = { ...props.initialFormState }
      } else {
        // Check localStorage for a saved draft
        const draft = loadDraft(currentDraftKey.value)
        if (draft) {
          form.value = { ...draft }
        } else if (props.document) {
          form.value = {
            title: props.document.title,
            type: props.document.type,
            content: props.document.content,
            isPublic: props.document.isPublic,
          }
        } else {
          form.value = {
            title: '',
            type: 'other',
            content: '',
            isPublic: true,
          }
        }
      }

      // Track as active document so it survives page reload (after form is set)
      setActiveDocument({
        entityType: props.entityType,
        entityId: props.entityId,
        documentId: props.document?.id ?? null,
        form: { ...form.value },
      })
      
      nextTick(() => setupCodeBlockObserver())
    } else {
      clearActiveDocument()
      if (codeBlockObserver) codeBlockObserver.disconnect()
    }
  },
  { immediate: true }
)

// --- Auto-save draft + active form to localStorage (debounced) ---
let draftTimer: ReturnType<typeof setTimeout> | null = null

watch(
  form,
  (newForm) => {
    if (!props.open) return
    if (draftTimer) clearTimeout(draftTimer)
    draftTimer = setTimeout(() => {
      saveDraft(currentDraftKey.value, { ...newForm })
      updateActiveForm({ ...newForm })
    }, 1000)
  },
  { deep: true }
)

// Flush form state immediately on page unload so nothing is lost
const flushOnUnload = () => {
  if (props.open) {
    saveDraft(currentDraftKey.value, { ...form.value })
    updateActiveForm({ ...form.value })
  }
}
window.addEventListener('beforeunload', flushOnUnload)

onBeforeUnmount(() => {
  if (draftTimer) clearTimeout(draftTimer)
  window.removeEventListener('beforeunload', flushOnUnload)
})

const isValid = computed(() => {
  return form.value.title.trim() !== '' && form.value.content.trim() !== ''
})

const handleSubmit = async () => {
  if (!isValid.value) return

  isSubmitting.value = true

  try {
    if (isEditing.value && props.document) {
      await documentsStore.updateDocument(props.document.id, {
        title: form.value.title,
        type: form.value.type,
        content: form.value.content,
        isPublic: form.value.isPublic,
      })
      toast({
        title: 'Documento actualizado',
        description: form.value.title,
      })
    } else {
      await documentsStore.createDocument({
        entityType: props.entityType,
        entityId: props.entityId,
        title: form.value.title,
        type: form.value.type,
        content: form.value.content,
        isPublic: form.value.isPublic,
      })
      toast({
        title: 'Documento creado',
        description: form.value.title,
      })
    }

    // Clear draft on successful save
    clearDraft(currentDraftKey.value)

    emit('success')
    emit('update:open', false)
  } catch (error) {
    console.error('Error saving document:', error)
    toast({
      title: 'Error al guardar documento',
      description: error instanceof Error ? error.message : 'Ocurrio un error inesperado',
      variant: 'destructive',
    })
  } finally {
    isSubmitting.value = false
  }
}

const handleMinimize = () => {
  // Clear standalone draft — the minimized-documents composable handles its own persistence
  clearDraft(currentDraftKey.value)
  emit('minimize', { ...form.value })
  emit('update:open', false)
}

const handleClose = () => {
  emit('update:open', false)
}

const handleEscapeOrOutside = (event: Event) => {
  event.preventDefault()
  handleMinimize()
}
</script>

<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent
      class="w-screen h-screen max-w-none max-h-none m-0 rounded-none flex flex-col [&>button.absolute]:hidden"
      @escape-key-down="handleEscapeOrOutside"
      @pointer-down-outside="handleEscapeOrOutside"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b px-6 py-4 shrink-0">
        <div>
          <DialogTitle class="text-xl">
            {{ isEditing ? 'Editar Documento' : 'Nuevo Documento' }}
          </DialogTitle>
          <DialogDescription>
            {{ isEditing ? 'Modifica el documento. Se incrementara la version automaticamente.' : 'Crea un nuevo documento en formato Markdown.' }}
          </DialogDescription>
          <div v-if="isEditing && document" class="flex items-center gap-1 mt-1 text-xs text-muted-foreground/70">
            <span class="font-mono text-[11px]">{{ document.id }}</span>
            <button
              class="inline-flex items-center justify-center h-4 w-4 rounded hover:bg-muted transition-colors"
              title="Copiar ID"
              @click="copyDocumentId"
            >
              <Copy class="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button variant="outline" @click="handleClose" :disabled="isSubmitting">
            {{ t('common.cancel') }}
          </Button>
          <Button variant="outline" @click="handleMinimize" :disabled="isSubmitting">
            <Minus class="mr-2 h-4 w-4" />
            Minimizar
          </Button>
          <Button @click="handleSubmit" :disabled="isSubmitting || !isValid">
            <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
            {{ isEditing ? t('common.save') : t('common.create') }}
          </Button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden flex flex-col px-6 py-4">
        <!-- Top Bar: Title, Type, Visibility -->
        <div class="flex items-end gap-4 mb-4 shrink-0">
          <div class="flex-1 space-y-1">
            <Label for="title">Titulo *</Label>
            <Input
              id="title"
              v-model="form.title"
              placeholder="Nombre del documento"
              :disabled="isSubmitting"
              class="text-lg"
            />
          </div>
          <div class="w-48 space-y-1">
            <Label>Tipo</Label>
            <Select v-model="form.type" :disabled="isSubmitting">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  v-for="option in documentTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="flex items-center gap-2 pb-1">
            <Switch
              id="isPublic"
              :checked="form.isPublic"
              @update:checked="form.isPublic = $event"
              :disabled="isSubmitting"
            />
            <Label for="isPublic" class="font-normal cursor-pointer whitespace-nowrap">
              {{ form.isPublic ? 'Publico' : 'Interno' }}
            </Label>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button 
                variant="outline" 
                :disabled="isSubmitting"
              >
                <Workflow class="w-4 h-4 mr-2" />
                Añadir Diagrama
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem 
                v-for="diagram in DIAGRAM_TYPES" 
                :key="diagram.id" 
                @click="openEditor(diagram)"
              >
                <component :is="diagram.icon" class="w-4 h-4 mr-2 text-primary" />
                {{ diagram.label }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <!-- Edit Existing Diagram Button -->
          <DropdownMenu v-if="existingDiagrams.length > 0">
            <DropdownMenuTrigger as-child>
              <Button 
                variant="outline" 
                class="border-primary text-primary"
                :disabled="isSubmitting"
              >
                <Workflow class="w-4 h-4 mr-2" />
                Editar Diagrama ({{ existingDiagrams.length }})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem 
                v-for="block in existingDiagrams" 
                :key="block.index" 
                @click="editExistingDiagram(block)"
              >
                <component :is="block.diagram.icon" class="w-4 h-4 mr-2 text-primary" />
                {{ block.title ? block.title : `Editar ${block.diagram.label} #${block.index + 1}` }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <!-- Editor (fills remaining space) -->
        <div class="flex-1 border rounded-md overflow-hidden">
          <MdEditor
            v-model="form.content"
            language="en-US"
            :preview="true"
            :toolbarsExclude="['github', 'save']"
            :disabled="isSubmitting"
            class="h-full"
            style="height: 100%"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
  
  <component 
    v-if="activeEditor"
    :is="activeEditor.component"
    v-model:open="showEditorModal"
    @save="handleDiagramSave"
  />
</template>

<style>
/* Override md-editor styles for dark mode compatibility */
.md-editor {
  --md-bk-color: hsl(var(--background));
  --md-color: hsl(var(--foreground));
  --md-border-color: hsl(var(--border));
}

.dark .md-editor {
  --md-bk-color: hsl(var(--background));
  --md-color: hsl(var(--foreground));
  --md-border-color: hsl(var(--border));
}
</style>
